import { useState } from "react";
import { createPortal } from "react-dom";
import DeleteConfirmModal from "./DeleteConfirmModal";

const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const GALLERY_CONFIRM_MODAL_ID = "gallery-image-confirm-modal";

const GalleryManager = ({ images = [], onUpload, onDelete, onMoveUp, onMoveDown }) => {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [previewSrc, setPreviewSrc] = useState(null);
  const [deleteId, setDeleteId] = useState(0);

  const sorted = [...images].sort((a, b) => a.displayOrder - b.displayOrder);

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;

    setError("");

    const validFiles = [];
    const errors = [];
    for (const file of files) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        errors.push(`${file.name}: please choose a JPG, PNG, or WEBP image`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name}: image must not exceed 5 MB`);
        continue;
      }
      validFiles.push(file);
    }

    if (errors.length > 0) setError(errors.join(" "));

    if (validFiles.length === 0) return;

    try {
      setBusy(true);
      for (const file of validFiles) {
        await onUpload(file);
      }
    } finally {
      setBusy(false);
    }
  };

  const confirmDeleteAsync = async () => {
    if (!deleteId) return;
    try {
      setBusy(true);
      await onDelete(deleteId);
    } finally {
      setBusy(false);
      setDeleteId(0);
    }
  };

  const handleMove = async (fn, imageId) => {
    try {
      setBusy(true);
      await fn(imageId);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="gallery-manager">
      <div className="d-flex flex-wrap gap-2 mb-2">
        {sorted.length === 0 && <small className="text-muted">No gallery images yet.</small>}
        {sorted.map((image, index) => (
          <div
            key={image.id}
            className="border rounded p-1"
            style={{ width: 120 }}
          >
            <img
              src={image.imagePath}
              alt="Gallery"
              className="cursor-pointer"
              style={{ width: "100%", height: 90, objectFit: "cover", borderRadius: 4 }}
              onClick={() => setPreviewSrc(image.imagePath)}
            />
            <div className="d-flex justify-content-between align-items-center mt-1">
              <i
                className={`fas fa-arrow-up cursor-pointer ${index === 0 ? "text-muted" : "text-primary"}`}
                onClick={() => index > 0 && !busy && handleMove(onMoveUp, image.id)}
              />
              <i
                className="fas fa-eye text-secondary cursor-pointer"
                onClick={() => setPreviewSrc(image.imagePath)}
              />
              <i
                className={`fas fa-arrow-down cursor-pointer ${index === sorted.length - 1 ? "text-muted" : "text-primary"}`}
                onClick={() => index < sorted.length - 1 && !busy && handleMove(onMoveDown, image.id)}
              />
              <i
                className="fas fa-trash text-danger cursor-pointer"
                data-bs-toggle="modal"
                data-bs-target={`#${GALLERY_CONFIRM_MODAL_ID}`}
                onClick={() => !busy && setDeleteId(image.id)}
              />
            </div>
          </div>
        ))}
      </div>
      <input
        type="file"
        className="form-control"
        accept="image/jpeg,image/png,image/webp"
        multiple
        onChange={handleFileChange}
        disabled={busy}
      />
      <small className="text-muted d-block mt-1">You can select multiple images at once.</small>
      <small className="text-danger">{error}</small>

      {createPortal(
        <DeleteConfirmModal
          id={GALLERY_CONFIRM_MODAL_ID}
          title="Delete Image"
          message="Are you sure you want to delete this gallery image?"
          deleteAsync={confirmDeleteAsync}
          setDeleteId={setDeleteId}
        />,
        document.body
      )}

      {previewSrc && createPortal(
        <div
          onClick={() => setPreviewSrc(null)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.85)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "zoom-out",
          }}
        >
          <button
            type="button"
            onClick={() => setPreviewSrc(null)}
            style={{
              position: "absolute",
              top: 20,
              right: 30,
              background: "transparent",
              border: "none",
              color: "#fff",
              fontSize: 32,
              lineHeight: 1,
            }}
            aria-label="Close"
          >
            &times;
          </button>
          <img
            src={previewSrc}
            alt="Gallery preview"
            style={{ maxWidth: "90%", maxHeight: "90%", objectFit: "contain" }}
          />
        </div>,
        document.body
      )}
    </div>
  );
};

export default GalleryManager;
